import { useRef, useState, useEffect, useMemo } from "react";
import { CheckPicker, DateRangePicker, InputNumber, SelectPicker } from "rsuite";
import { Button as CustomButton } from "..";
import "./index.css";

function isDateColumn(columnType) {
  return columnType === "date";
}

function isNumberColumn(columnType) {
  return columnType === "number";
}

function FilterPanel({
  activeColumn,
  columnType = "string",
  filterOptions = [],
  selectedValues = [],
  onApply,
  onCancel,
  onFilterChange
}) {
  const filterCheckboxRef = useRef(null);
  const [dateRangeValue, setDateRangeValue] = useState(null);
  
  // Normalize option values and selected values to strings for consistent matching in CheckPicker
  const normalizedOptions = useMemo(() => {
    return (filterOptions || []).map((opt) => ({
      ...opt,
      // Keep original for mapping back, but ensure the value used by the picker is a string
      originalValue: opt.value,
      value: opt.value === null || opt.value === undefined ? "" : String(opt.value)
    }));
  }, [filterOptions]);

  // Map for converting string value back to original typed value
  const valueLookup = useMemo(() => {
    const map = new Map();
    for (const opt of normalizedOptions) {
      map.set(opt.value, opt.originalValue);
    }
    return map;
  }, [normalizedOptions]);

  const normalizedSelectedValues = useMemo(() => {
    if (!Array.isArray(selectedValues)) return [];
    return selectedValues.map((v) => (v === null || v === undefined ? "" : String(v)));
  }, [selectedValues]);

  // Local state to keep the picker interactive even if parent doesn't echo back selections immediately
  const [pickerValues, setPickerValues] = useState(normalizedSelectedValues);

  // Keep local state in sync when external selectedValues change (e.g., when reopening panel)
  useEffect(() => {
    setPickerValues(normalizedSelectedValues);
  }, [normalizedSelectedValues, normalizedOptions]);
  
  // Number filter state
  const [numberOperator, setNumberOperator] = useState("equals");
  const [numberValue, setNumberValue] = useState("");
  const [numberValue2, setNumberValue2] = useState("");

  // Initialize and update values when selectedValues change
  useEffect(() => {
    if (isDateColumn(columnType)) {
      if (Array.isArray(selectedValues) && selectedValues.length === 2) {
        const startDate = selectedValues[0] instanceof Date ? selectedValues[0] : (selectedValues[0] ? new Date(selectedValues[0]) : null);
        const endDate = selectedValues[1] instanceof Date ? selectedValues[1] : (selectedValues[1] ? new Date(selectedValues[1]) : null);
        
        if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          setDateRangeValue([startDate, endDate]);
        } else {
          setDateRangeValue(null);
        }
      } else {
        setDateRangeValue(null);
      }
    } else if (isNumberColumn(columnType)) {
      // Initialize number filter from selectedValues
      if (selectedValues && typeof selectedValues === 'object' && selectedValues.operator) {
        setNumberOperator(selectedValues.operator);
        if (selectedValues.values && selectedValues.values.length > 0) {
          setNumberValue(String(selectedValues.values[0]));
          if (selectedValues.values.length > 1) {
            setNumberValue2(String(selectedValues.values[1]));
          }
        }
      } else {
        setNumberOperator("equals");
        setNumberValue("");
        setNumberValue2("");
      }
    }
  }, [selectedValues, columnType]);

  const handleDateRangeChange = (range) => {
    setDateRangeValue(range);
    onFilterChange(range);
  };

  const handleNumberFilterApply = () => {
    const num1 = parseFloat(numberValue);
    const num2 = parseFloat(numberValue2);
    
    let filterData = {
      operator: numberOperator,
      values: []
    };

    if (!isNaN(num1)) {
      if (numberOperator === "between" && !isNaN(num2)) {
        filterData.values = [Math.min(num1, num2), Math.max(num1, num2)];
      } else {
        filterData.values = [num1];
      }
    }

    // Update the filter values and then apply
    onFilterChange(filterData);
    // Apply the filter immediately
    onApply();
  };

  const clearNumberFilter = () => {
    setNumberValue("");
    setNumberValue2("");
    setNumberOperator("equals");
    onFilterChange({ operator: "equals", values: [] });
    // Apply the cleared filter immediately
    onApply();
  };

  const numberOperators = [
    { label: "Equals", value: "equals" },
    { label: "Greater than", value: "greater" },
    { label: "Less than", value: "less" },
    { label: "Greater than or equal", value: "greaterEqual" },
    { label: "Less than or equal", value: "lessEqual" },
    { label: "Between", value: "between" },
    { label: "Not equals", value: "notEquals" }
  ];

  return (
    <div
      className="checkpicker-container"
      data-testid="filter-panel"
      style={{
        marginBottom: "10px",
        padding: "10px",
        border: "1px solid #e5e5ea",
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap"

      }}
    >
      <label
        style={{
          fontWeight: "bold",
          minWidth: "fit-content"
        }}
      >
        Filter by {activeColumn}:
      </label>
      
      {isDateColumn(columnType) ? (
        <DateRangePicker
          data-testid="filter-select-date-range"
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          cleanable={false}
          format="dd/MM/yyyy"
          placeholder="DD/MM/YYYY - DD/MM/YYYY"
          style={{ width: 300 }}
        />
      ) : isNumberColumn(columnType) ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <SelectPicker
            data={numberOperators}
            value={numberOperator}
            onChange={setNumberOperator}
            style={{ width: 150 }}
            placeholder="Operator"
            searchable={false}
            data-testid="filter-select-number-operator"
          />
          <InputNumber
            type="number"
            value={numberValue}
            onChange={setNumberValue}
            placeholder="Value"
            className="custom-number-input"
            style={{ width: 100 }}
            data-testid="filter-input-number-value"
          />
          {numberOperator === "between" && (
            <>
              <span style={{ color: "#666" }}>and</span>
              <InputNumber
                type="number"
                value={numberValue2}
                onChange={setNumberValue2}
                placeholder="Value 2"
                className="custom-number-input"
                style={{ width: 100 }}
                data-testid="filter-input-number-value2"
              />
              
            </>
          )}
          <CustomButton 
            text="Apply" 
            onClick={handleNumberFilterApply} 
            size="sm"
            disabled={!numberValue || (numberOperator === "between" && !numberValue2)}
          >
            Apply
          </CustomButton>
          <CustomButton 
            text="Clear" 
            onClick={clearNumberFilter} 
            color="red"
            size="sm"
          >
            Clear
          </CustomButton>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
          <CheckPicker
            data={normalizedOptions}
            // Make the component controlled to reflect external changes reliably
            value={pickerValues}
            defaultValue={normalizedSelectedValues}
            onChange={(vals) => {
              // Update local interactive state first
              setPickerValues(vals || []);
              // vals are strings; convert back to original types using lookup
              const mapped = (vals || []).map((v) => (valueLookup.has(v) ? valueLookup.get(v) : v));
              onFilterChange(mapped);
            }}
            ref={filterCheckboxRef}
            onClean={() => {
              setPickerValues([]);
              onFilterChange([]);
            }}
            placeholder="Select values"
            style={{ width: 450 }}
            preventOverflow
            cleanable
            virtualized
            searchable
            searchBy={(keyword, label, item) => {
              // Trim whitespace from search keyword
              const trimmedKeyword = keyword.trim().toLowerCase();
              if (!trimmedKeyword) return true;
              
              // Search in both label and value, also trimming item text
              const itemLabel = (item.label || '').toString().trim().toLowerCase();
              const itemValue = (item.value || '').toString().trim().toLowerCase();
              
              return itemLabel.includes(trimmedKeyword) || itemValue.includes(trimmedKeyword);
            }}
            listProps={{
              height: 300,
              rowHeight: 30,
              bordered: false,
              hover: false,
            }}
            menuStyle={{
              padding: 0,
            }}
          />
        </div>
      )}
      
      {!isNumberColumn(columnType) && (
        <>
          <CustomButton text="Apply" onClick={onApply} className="filter-button">
            Apply
          </CustomButton>
          <CustomButton text="Cancel" color="blue" onClick={onCancel} className="filter-button">
            Cancel
          </CustomButton>
        </>
      )}
      
      {isNumberColumn(columnType) && (
        <CustomButton text="Cancel" color="blue" onClick={onCancel} className="filter-button">
          Cancel
        </CustomButton>
      )}
    </div>
  );
}

export default FilterPanel;
